import type { ConnectionSuggestion } from '@/features/home/hooks/useConnectionSuggestions';

export type SuggestionPill =
  | 'recommended'
  | 'gym'
  | 'profession'
  | 'mutuals'
  | 'interests'
  | 'location';

export interface DiscoverySignals {
  main_academy_id: string | null;
  profession: string | null;
  has_interests: boolean;
  hobby_ids: number[];
  city: string | null;
  state: string | null;
  country: string | null;
}

const norm = (v?: string | null) => v?.trim().toLowerCase() || '';

export function locationProximityScore(
  suggestion: ConnectionSuggestion,
  signals: Pick<DiscoverySignals, 'city' | 'state' | 'country'>,
): number {
  const myCity = norm(signals.city);
  const myState = norm(signals.state);
  const myCountry = norm(signals.country);
  if (!myCountry) return 0;

  const city = norm(suggestion.city);
  const state = norm(suggestion.state);
  const country = norm(suggestion.country);

  if (myCity && city && city === myCity) return 3;
  if (myState && state && state === myState) return 2;
  if (country && country === myCountry) return 1;
  return 0;
}

export function sortByLocationProximity(
  suggestions: ConnectionSuggestion[],
  signals: Pick<DiscoverySignals, 'city' | 'state' | 'country'>,
): ConnectionSuggestion[] {
  return [...suggestions]
    .map(s => ({ s, score: locationProximityScore(s, signals) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ s }) => s);
}

export function sortRecommended(suggestions: ConnectionSuggestion[]): ConnectionSuggestion[] {
  return [...suggestions].sort((a, b) => {
    const mutualDiff = (b.mutual_count ?? 0) - (a.mutual_count ?? 0);
    if (mutualDiff !== 0) return mutualDiff;
    const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    return bTime - aTime;
  });
}

export function filterBySharedInterests(
  suggestions: ConnectionSuggestion[],
  myHobbyIds: number[],
): ConnectionSuggestion[] {
  if (myHobbyIds.length === 0) return [];
  const mine = new Set(myHobbyIds);
  return suggestions.filter(s =>
    (s.hobby_ids ?? []).some(id => mine.has(id)),
  );
}

export function filterSuggestionsByPill(
  suggestions: ConnectionSuggestion[],
  pill: SuggestionPill,
  signals: DiscoverySignals | null,
): ConnectionSuggestion[] {
  switch (pill) {
    case 'recommended':
      return sortRecommended(suggestions);
    case 'gym': {
      if (!signals?.main_academy_id) return [];
      const gymMates = suggestions.filter(
        s => s.main_academy_id && s.main_academy_id === signals.main_academy_id,
      );
      return gymMates.length > 0 ? gymMates : suggestions;
    }
    case 'profession': {
      if (!signals?.profession) return [];
      const sameProf = suggestions.filter(s => s.profession === signals.profession);
      return sameProf.length > 0 ? sameProf : suggestions;
    }
    case 'mutuals':
      return suggestions
        .filter(s => (s.mutual_count ?? 0) > 0)
        .sort((a, b) => (b.mutual_count ?? 0) - (a.mutual_count ?? 0));
    case 'interests':
      if (!signals?.has_interests) return [];
      return filterBySharedInterests(suggestions, signals.hobby_ids);
    case 'location':
      if (!signals?.country) return [];
      return sortByLocationProximity(suggestions, signals);
    default:
      return suggestions;
  }
}

export function bucketSuggestions(
  suggestions: ConnectionSuggestion[],
  signals: DiscoverySignals | null,
) {
  const available = suggestions;
  return {
    peopleYouMightKnow: sortRecommended(available).slice(0, 8),
    gymMates: signals?.main_academy_id
      ? available.filter(s => s.main_academy_id === signals.main_academy_id)
      : [],
    sameProfession: signals?.profession
      ? available.filter(s => s.profession === signals.profession)
      : [],
    mutuals: available
      .filter(s => (s.mutual_count ?? 0) > 0)
      .sort((a, b) => (b.mutual_count ?? 0) - (a.mutual_count ?? 0)),
    sameInterests: signals?.has_interests
      ? filterBySharedInterests(available, signals.hobby_ids)
      : [],
    nearYou: signals?.country
      ? sortByLocationProximity(available, signals)
      : [],
  };
}
